import { t } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import MModal from 'Components/modal/MModal/MModal';
import { AppDispatch } from 'Libs/redux/store';
import { useTypedSelector } from 'Libs/redux/utils';
import { enqueueBasicAlert } from 'Pages/AlertManager';
import { ALERT_DURATION } from 'Pages/Content/GameplaysForm/gameplay.constants';
import { selectActivityEditionError, selectActivityEditionSuccess } from 'Store/entities/activity/activity.selectors';
import { activityActions } from 'Store/entities/activity/activity.slice';
import { archiveActivity, unarchiveActivity } from 'Store/entities/activity/activity.thunks';
import { ActivityEditionResponse } from 'Store/entities/activity/activity.type';

const useArchiveAction = () => {
  const [visibleArchiveWarningModal, displayArchiveWarningModal] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState<number>(0);

  const dispatch = useDispatch<AppDispatch>();

  const error = useTypedSelector(selectActivityEditionError);
  const success = useTypedSelector(selectActivityEditionSuccess);

  useEffect(() => {
    return () => {
      dispatch(activityActions.resetEdition());
    };
  }, []);

  useEffect(() => {
    if (error) {
      let title = '';
      if (error === ActivityEditionResponse.ARCHIVE_ERROR) {
        title = t('activities:alerts.errors.archive_error_message');
      }
      if (error === ActivityEditionResponse.UNARCHIVE_ERROR) {
        title = t('activities:alerts.errors.unarchive_error_message');
      }

      enqueueBasicAlert(
        {
          id: 'campaign_edition_failed',
          status: 'error',
          title,
        },
        ALERT_DURATION,
      );
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      let title = '';
      if (success === ActivityEditionResponse.ARCHIVE_SUCCESS) {
        title = t('activities:alerts.success.archive');
      }
      if (success === ActivityEditionResponse.UNARCHIVE_SUCCESS) {
        title = t('activities:alerts.success.unarchive');
      }

      enqueueBasicAlert(
        {
          id: 'campaign_edition_success',
          status: 'success',
          title,
        },
        ALERT_DURATION,
      );
    }
  }, [success]);

  const handleArchive = useCallback(
    (id: number) => {
      dispatch(archiveActivity({ id }));
      setSelectedActivityId(0);
    },
    [dispatch],
  );

  const handleUnarchive = useCallback(
    (id: number) => {
      dispatch(unarchiveActivity({ id }));
    },
    [dispatch],
  );

  const openArchiveModal = useCallback(() => displayArchiveWarningModal(true), [displayArchiveWarningModal]);

  const handleArchiveClick = useCallback((id: number, isArchived: boolean) => {
    if (isArchived) {
      return handleUnarchive(id);
    }

    setSelectedActivityId(id);
    displayArchiveWarningModal(true);
  }, []);

  const archiveWarningModal = (
    <MModal
      onCloseModal={() => displayArchiveWarningModal(false)}
      visible={visibleArchiveWarningModal}
      type="dialog"
      onSecondAction={() => displayArchiveWarningModal(false)}
      showExitCross
      actionButtonType="destructive"
      title={t('activities:modals.before_archive_warning.title')}
      description={t('activities:modals.before_archive_warning.description')}
      labelActionButton={t('activities:modals.before_archive_warning.action_button')}
      labelSecondButton={t('activities:modals.before_archive_warning.second_button')}
      onAction={() => {
        displayArchiveWarningModal(false);
        handleArchive(selectedActivityId);
      }}
    />
  );

  return {
    visibleArchiveWarningModal,
    displayArchiveWarningModal,
    archiveWarningModal,
    handleArchive,
    handleUnarchive,
    openArchiveModal,
    handleArchiveClick,
  };
};

export default useArchiveAction;
